// L'EFFACEUR — NextActionCard
// CCC.9 : carte CTA "Prochaine action de votre côté" sur l'accueil dashboard.
// Idée : le client doit savoir EXACTEMENT quoi faire MAINTENANT pour faire avancer
// son dossier, sans chercher dans les onglets. Si rien à faire → message rassurant
// "Rien de votre côté — on s'occupe de tout, on vous tient au courant".

import { motion } from 'framer-motion';
import { ArrowRight, FileText, PenTool, Upload, Clock, Sparkles, CheckCircle, Briefcase } from 'lucide-react';

const PACK_LABELS = {
  mission_demenagement: 'Déménagement',
  nouveau_depart: 'Déménagement',
  mission_heritage: 'Héritage',
  serenite_succession: 'Héritage',
  mission_succession: 'Héritage',
  mission_audit: 'Audit',
  audit_pouvoir_achat: 'Audit',
};

const PACK_ROUTES = {
  mission_demenagement: '/missions/demenagement',
  nouveau_depart: '/missions/demenagement',
  mission_heritage: '/missions/succession',
  serenite_succession: '/missions/succession',
  mission_succession: '/missions/succession',
  mission_audit: '/audit',
  audit_pouvoir_achat: '/audit',
};

// Détermine l'action prioritaire à partir du status de la mission.
// Retourne { icon, title, desc, cta, route } ou null si rien à faire.
const computeNextAction = (mission) => {
  if (!mission) return null;
  const status = (mission.admin_status || mission.status || '').toLowerCase();
  const packLabel = PACK_LABELS[mission.pack_type] || 'Mission';
  const route = PACK_ROUTES[mission.pack_type] || '/dashboard';
  const currentStep = mission.current_step || 1;

  // Documents manquants
  const missingDocs = mission.missing_docs || mission.documents_missing || [];
  if (missingDocs.length > 0) {
    return {
      icon: Upload,
      color: 'orange',
      title: `Téléverser ${missingDocs.length} document${missingDocs.length > 1 ? 's' : ''} manquant${missingDocs.length > 1 ? 's' : ''}`,
      desc: `Mission ${packLabel} — ${missingDocs.slice(0, 2).join(', ')}${missingDocs.length > 2 ? '…' : ''}`,
      cta: 'Téléverser',
      route,
    };
  }

  // Mandat à signer
  if (status.includes('mandat') && (status.includes('attente') || status.includes('signer'))) {
    return {
      icon: PenTool,
      color: 'cyan',
      title: 'Signer votre mandat',
      desc: `Mission ${packLabel} — signature électronique horodatée (art. 1367 C. civ.)`,
      cta: 'Signer',
      route,
    };
  }

  // Tunnel à compléter
  if (status === 'nouveau' || status === 'en_creation' || currentStep < 4) {
    return {
      icon: FileText,
      color: 'cyan',
      title: 'Compléter votre dossier',
      desc: `Mission ${packLabel} — étape ${currentStep}/4`,
      cta: 'Reprendre',
      route,
    };
  }

  // En cours côté équipe
  if (status === 'en_cours' || status === 'en_traitement' || status === 'dispatch') {
    return {
      icon: Clock,
      color: 'success',
      title: 'On s\'occupe de tout',
      desc: `Mission ${packLabel} — résiliations en cours auprès des organismes. Vous serez notifié à chaque étape.`,
      cta: 'Voir le suivi',
      route,
    };
  }

  // Terminé
  if (status === 'termine' || status === 'efface' || status === 'cloture') {
    return {
      icon: CheckCircle,
      color: 'success',
      title: 'Mission terminée',
      desc: `Mission ${packLabel} — toutes les démarches ont été effectuées.`,
      cta: 'Voir le récap',
      route,
    };
  }

  return null;
};

const COLOR_MAP = {
  cyan: {
    border: 'border-cyan-400/40',
    bg: 'bg-cyan-400/5',
    icon: 'text-cyan-400',
    iconBg: 'bg-cyan-400/15 border-cyan-400/30',
    cta: 'bg-cyan-400 hover:bg-cyan-300 text-black',
  },
  orange: {
    border: 'border-orange-400/40',
    bg: 'bg-orange-400/5',
    icon: 'text-orange-400',
    iconBg: 'bg-orange-400/15 border-orange-400/30',
    cta: 'bg-orange-400 hover:bg-orange-300 text-black',
  },
  success: {
    border: 'border-success/30',
    bg: 'bg-success/5',
    icon: 'text-success',
    iconBg: 'bg-success/15 border-success/30',
    cta: 'bg-success hover:bg-success/90 text-black',
  },
  magenta: {
    border: 'border-magenta-400/40',
    bg: 'bg-magenta-400/5',
    icon: 'text-magenta-400',
    iconBg: 'bg-magenta-400/15 border-magenta-400/30',
    cta: 'bg-magenta-400 hover:bg-magenta-300 text-black',
  },
};

const NextActionCard = ({ missions = [], setActiveTab, onNavigate }) => {
  // Trouve la mission qui demande une action
  const actionable = missions
    .map((m) => ({ mission: m, action: computeNextAction(m) }))
    .filter((x) => x.action !== null);

  // Priorité : orange (urgent) > cyan (en cours user) > success (info)
  const PRIORITY = { orange: 0, cyan: 1, success: 2, magenta: 3 };
  actionable.sort((a, b) => PRIORITY[a.action.color] - PRIORITY[b.action.color]);

  // Aucune mission active → CTA démarrer
  if (missions.length === 0) {
    return (
      <CyberCard delay={0.12} testid="next-action-card" colorClass={COLOR_MAP.cyan}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0 relative">
            <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-lg" />
            <Sparkles className="w-6 h-6 text-cyan-400 relative" />
          </div>
          <div className="flex-1">
            <p className="text-cyan-400 text-xs font-mono uppercase mb-0.5 tracking-[0.2em]">// PROCHAINE_ACTION</p>
            <p className="text-white text-base font-bold">Démarrer votre première mission</p>
            <p className="text-gray-400 text-sm">
              Audit pouvoir d'achat <span className="text-success font-bold">gratuit</span>, ou pack Déménagement / Héritage.
            </p>
          </div>
          <button
            onClick={() => setActiveTab('payment')}
            className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-sm shadow-[0_0_20px_rgba(0,245,255,0.3)]"
            data-testid="start-mission-btn"
          >
            Démarrer <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </CyberCard>
    );
  }

  // Aucune action requise (toutes terminées) → message rassurant
  if (actionable.length === 0) {
    return (
      <CyberCard delay={0.12} testid="next-action-card" colorClass={COLOR_MAP.success}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-success/15 border border-success/30 flex items-center justify-center flex-shrink-0 relative">
            <div className="absolute inset-0 bg-success/20 blur-xl rounded-lg" />
            <CheckCircle className="w-6 h-6 text-success relative" />
          </div>
          <div className="flex-1">
            <p className="text-success text-xs font-mono uppercase mb-0.5 tracking-[0.2em]">// AUCUNE_ACTION_REQUISE</p>
            <p className="text-white text-base font-bold">Rien de votre côté</p>
            <p className="text-gray-400 text-sm">On s'occupe de tout. Vous serez notifié à chaque étape.</p>
          </div>
        </div>
      </CyberCard>
    );
  }

  const { action } = actionable[0];
  const Icon = action.icon;
  const color = COLOR_MAP[action.color] || COLOR_MAP.cyan;

  return (
    <CyberCard delay={0.12} testid="next-action-card" colorClass={color}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg ${color.iconBg} border flex items-center justify-center flex-shrink-0 relative`}>
          <div className={`absolute inset-0 ${color.bg.replace('/5', '/20')} blur-xl rounded-lg`} />
          <Icon className={`w-6 h-6 ${color.icon} relative`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-mono uppercase mb-0.5 tracking-[0.2em] ${color.icon}`}>// PROCHAINE_ACTION</p>
          <p className="text-white text-base font-bold">{action.title}</p>
          <p className="text-gray-400 text-sm truncate">{action.desc}</p>
        </div>
        <button
          onClick={() => { if (onNavigate) onNavigate(action.route); else window.location.href = action.route; }}
          className={`${color.cta} font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-sm flex-shrink-0 shadow-[0_0_20px_rgba(0,245,255,0.25)]`}
          data-testid="next-action-cta"
        >
          {action.cta} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {actionable.length > 1 && (
        <p className="text-xs text-gray-500 mt-3 ml-16">
          + {actionable.length - 1} autre{actionable.length - 1 > 1 ? 's' : ''} action{actionable.length - 1 > 1 ? 's' : ''} en attente
        </p>
      )}
    </CyberCard>
  );
};

// CCC.13 — wrapper "scan-laser cyber" (style AuditPackPage) : grid background + scan line + glow
const CyberCard = ({ children, delay = 0.12, testid, colorClass }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    data-testid={testid}
    className={`relative overflow-hidden rounded-xl border-2 ${colorClass?.border || 'border-cyan-400/40'} ${colorClass?.bg || 'bg-cyan-400/5'} p-5`}
  >
    {/* Grid de fond */}
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(rgba(0,245,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,245,255,0.5) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}
    />
    {/* Scan-line décorative en haut */}
    <div
      className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 pointer-events-none"
      style={{ animation: 'cyber-scan 4s linear infinite' }}
    />
    <style>{`
      @keyframes cyber-scan {
        0% { transform: translateY(0); opacity: 0.4; }
        50% { transform: translateY(80px); opacity: 0.9; }
        100% { transform: translateY(0); opacity: 0.4; }
      }
    `}</style>
    <div className="relative">{children}</div>
  </motion.div>
);

export default NextActionCard;
